import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, Lock, ArrowRight } from "lucide-react";
import { SiteShell } from "@/components/layout/SiteShell";
import { api, formatApiError } from "@/lib/api";
import { TID } from "@/constants/testIds";

const CATS = [
  { key: "communication", label: "Communication" },
  { key: "thoroughness", label: "Thoroughness" },
  { key: "constructive_feedback", label: "Constructive feedback" },
  { key: "professionalism", label: "Professionalism" },
  { key: "would_hire_again", label: "Would hire again" },
];

export default function ReviewsPage() {
  const [rows, setRows] = useState({ received: [], given: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("received");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/reviews/mine"); setRows(data); }
      catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const list = rows[tab];

  return (
    <SiteShell>
      <section className="container-page py-14" data-testid={TID.reviews.root}>
        <h1 className="font-serif text-4xl tracking-tight">Reviews</h1>
        <p className="mt-2 text-muted-foreground">Feedback across five categories — communication, thoroughness, constructive feedback, professionalism, and would-hire-again.</p>

        <div className="mt-8 inline-flex rounded-full border p-1 bg-card">
          {["received", "given"].map((v) => (
            <button key={v} onClick={() => setTab(v)}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${tab === v ? "bg-[hsl(var(--sb-forest))] text-white" : "text-foreground/70 hover:bg-muted"}`}>
              {v === "received" ? "Received" : "Given"}
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-4">
          {loading && Array.from({ length: 2 }).map((_, i) => <div key={i} className="card-soft h-40 animate-pulse" />)}
          {!loading && list.length === 0 && (
            <div className="card-soft text-center py-16">
              <h3 className="font-serif text-2xl">No reviews {tab === "received" ? "yet" : "given"}</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Reviews unlock after you complete a project together.
              </p>
            </div>
          )}
          {list.map((r) => <ReviewCard key={r.id} r={r} tab={tab} />)}
        </div>
      </section>
    </SiteShell>
  );
}

function ReviewCard({ r, tab }) {
  // Reveal-safe stubs come back from the backend for "received" reviews
  // the current user isn't yet allowed to see (double-blind). Only the
  // date + reveal-eligible date are exposed; content is sealed until the
  // user submits their counterpart review OR 14 days elapse.
  if (r.revealed === false) {
    return (
      <div
        className="card-soft border-dashed"
        data-testid={`reviews-hidden-card-${r.id}`}
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-muted grid place-items-center text-muted-foreground shrink-0">
            <Lock size={14} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium">A review is waiting to unlock</div>
            <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
              You&apos;ll see this review once you leave your own for the same project — or automatically on{" "}
              <strong>
                {r.reveals_at ? new Date(r.reveals_at).toLocaleDateString() : "the 14-day mark"}
              </strong>
              , whichever comes first.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Submitted {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
            </p>
            {r.job_id && (
              <Link
                to={`/jobs/${r.job_id}?review=1`}
                className="btn-primary mt-4 inline-flex items-center gap-1.5 text-sm"
                data-testid={`reviews-hidden-leave-${r.id}`}
              >
                Leave your review <ArrowRight size={14} />
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }
  return <RevealedReviewCard r={r} />;
}

function RevealedReviewCard({ r }) {
  return (
    <div className="card-soft">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="font-medium">{r.reviewer_name}</div>
          <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
        </div>
        <div className="inline-flex items-center gap-1 font-serif text-2xl">
          <Star size={18} className="fill-[hsl(var(--sb-clay))] text-[hsl(var(--sb-clay))]" />
          {r.average?.toFixed(2)}
        </div>
      </div>
      <p className="mt-4 text-sm leading-relaxed whitespace-pre-line">{r.body}</p>
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
        {CATS.map((c) => (
          <div key={c.key} className="rounded-xl border px-3 py-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{c.label}</div>
            <div className="mt-1 font-medium">{r.ratings?.[c.key]} / 5</div>
          </div>
        ))}
      </div>
    </div>
  );
}
